/**
 * A user as the backoffice sees it.
 *
 * `pk` is the primary key. Do NOT use `User.id`, which holds the national ID
 * (DNI/RUT) and is unrelated to identity in the API routes.
 */
export interface AdminUser {
  pk: string;
  email: string;
  name: string;
  // UserDTO's `roles` is nullable on the backend, even though UserModel.roles
  // is not and serializes as `[]` in practice. Read as `(user.roles ?? [])`.
  roles: string[] | null;
  banned: boolean;
  deleted: boolean | null;
  createdDate: string | null;
}

/**
 * The flat page shape every backoffice screen consumes. Normalized by
 * `toPageResponse` — this is NOT necessarily what the API sends.
 */
export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

/**
 * Spring serializes a Page in two different shapes, and which one you get is an
 * environment setting (`spring.data.web.pageable.serialization-mode`), not
 * something this repo pins:
 *
 *   flat   {content, totalElements, totalPages, number, size}   — the legacy
 *          shape, deprecated in Spring Data
 *   nested {content, page: {size, number, totalElements, totalPages}} — where
 *          Spring is heading
 *
 * Reading `totalPages` off the root against a nested payload yields `undefined`,
 * and that value is poison downstream: `Math.max(undefined, 1)` renders NaN and
 * `page + 1 >= undefined` is false, so the pager showed "Página 1 de NaN" with
 * Next permanently enabled. Accepting both shapes here is deliberate — the
 * screens should not have to know which backend they happen to be talking to.
 */
export const toPageResponse = <T>(data: unknown): PageResponse<T> => {
  const raw = (data ?? {}) as Record<string, unknown>;
  const nested = (raw.page ?? {}) as Record<string, unknown>;

  const pick = (key: string): number => {
    const value = raw[key] ?? nested[key];
    return typeof value === 'number' ? value : 0;
  };

  return {
    // Defaulting to [] rather than trusting the payload: a 200 carrying
    // something unexpected (an HTML error page from a proxy, say) would
    // otherwise reach the tables as undefined and throw on .map.
    content: Array.isArray(raw.content) ? (raw.content as T[]) : [],
    totalElements: pick('totalElements'),
    totalPages: pick('totalPages'),
    number: pick('number'),
    size: pick('size'),
  };
};
